import { useCallback, type PropsWithChildren } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CloudDownloadIcon, LoaderCircleIcon } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import type {
  Document,
  TitleHolder,
  TitleHolderType,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import {
  ButtonRebrand,
  CheckboxInputConnected,
  FormLabel,
} from "@tudigo-monorepo/web-tudigo-components";

import { ParticipationOrganizationTypeBadge } from "../../participation-organization-type-badge";
import { ParticipationTitleHolderInfos } from "../../participation-title-holder-infos";

const FormSchema = z.record(z.string(), z.boolean());

type FormSchemaType = z.infer<typeof FormSchema>;

function TitleHolderCheckboxFields({
  type,
  titleHolders,
}: {
  type: TitleHolderType;
  titleHolders: TitleHolder[];
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-y-6 rounded-lg border border-neutral-200 p-4">
      <p className="body500-bold text-black">
        {t(
          `participations.web_app.participations_list_page.generate_participation_documents.dialog.form.${type}.title`,
        )}
      </p>
      <ul className="flex flex-col gap-y-6">
        {titleHolders.map((titleHolder) => (
          <li
            key={`li-${titleHolder.id}`}
            className="flex items-center gap-x-2"
          >
            <FormLabel
              htmlFor={titleHolder.id}
              className="flex items-center gap-x-2"
            >
              <CheckboxInputConnected
                id={titleHolder.id}
                name={titleHolder.id}
              />
              <ParticipationTitleHolderInfos titleHolder={titleHolder} />
              <ParticipationOrganizationTypeBadge
                type={titleHolder.ownerOrganization.type}
              />
            </FormLabel>
          </li>
        ))}
      </ul>
    </div>
  );
}

type GenerateParticipationDocumentsFormProps = PropsWithChildren<{
  titleHolders: TitleHolder[];
  onSubmit: (data: FormSchemaType) => Promise<Document>;
  isLoading?: boolean;
}>;

export function GenerateParticipationDocumentsForm({
  children,
  titleHolders,
  onSubmit,
  isLoading,
}: GenerateParticipationDocumentsFormProps) {
  const { t } = useTranslation();
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
  });
  const isSubmitDisabled = Object.values(form.watch()).every(
    (checked) => !checked,
  );
  const shareholderFields = titleHolders.filter(
    (titleHolder) => titleHolder.type === "share",
  );
  const bondHolderFields = titleHolders.filter(
    (titleHolder) => titleHolder.type === "bond",
  );
  const handleSubmit = useCallback(() => {
    onSubmit(form.watch()).then((response) => {
      form.reset();
      window.open(response.signedUrl, "_tab");

      return response;
    });
  }, [form, onSubmit]);

  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col gap-y-4"
        onSubmit={(e) => e.preventDefault()}
      >
        {shareholderFields.length > 0 && (
          <TitleHolderCheckboxFields
            type="share"
            titleHolders={shareholderFields}
          />
        )}
        {bondHolderFields.length > 0 && (
          <TitleHolderCheckboxFields
            type="bond"
            titleHolders={bondHolderFields}
          />
        )}
        {children}
        <ButtonRebrand
          variant="accent"
          className="ml-auto mr-6 mt-6 w-fit"
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isLoading}
        >
          {t(
            "participations.web_app.participations_list_page.generate_participation_documents.dialog.form.submit.label",
          )}
          {isLoading ? (
            <LoaderCircleIcon className="ml-2 size-4 animate-spin" />
          ) : (
            <CloudDownloadIcon className="ml-2 size-4" />
          )}
        </ButtonRebrand>
      </form>
    </FormProvider>
  );
}
